import { lazy, Suspense, useEffect, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Car, House, Settings as SettingsIcon, Trophy, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { isSignedIn } from '../auth/session';
import { MagicLinkForm } from '../auth/magic-link';
import { api, type PalProfileResponse } from '../cloud/api';
import { OnboardingWizard } from './OnboardingWizard';
import { VibeHome, type HomeNavigationTarget } from './VibeHome';
import { QuestDashboard } from './QuestDashboard';
import { VibeProfileScreen } from './VibeProfileScreen';
import { SettingsScreen } from './SettingsScreen';
import { DriveTab } from './DriveTab';
import { useAskMojiThread } from './hooks/useAskMojiThread';
import { useThemePreference, type UseThemePreferenceState } from './hooks/useThemePreference';
import { NaviPulseMark } from './NaviPulseMark';

const DevFaceGrid = import.meta.env.DEV
    ? lazy(() => import('./dev/FaceGrid').then((module) => ({ default: module.FaceGrid })))
    : null;
const DevFaceGridCartoon = import.meta.env.DEV
    ? lazy(() => import('./dev/FaceGridCartoon').then((module) => ({ default: module.FaceGridCartoon })))
    : null;
const DevVoiceHarness = import.meta.env.DEV
    ? lazy(() => import('./dev/VoiceDevHarness').then((module) => ({ default: module.VoiceDevHarness })))
    : null;

type AuthState =
    | { kind: 'checking' }
    | { kind: 'signed-out' }
    | { kind: 'dev-face-grid'; which: 'cartoon' | 'oled' }
    | { kind: 'signed-in'; pal: PalProfileResponse | 'loading' | 'needs-onboard' };

type Tab = 'home' | 'drive' | 'quests' | 'profile' | 'settings';

const TABS: ReadonlyArray<{ id: Tab; label: string; Icon: LucideIcon }> = [
    { id: 'home',     label: 'Home',     Icon: House },
    { id: 'drive',    label: 'Drive',    Icon: Car },
    { id: 'quests',   label: 'Quests',   Icon: Trophy },
    { id: 'profile',  label: 'Profile',  Icon: User },
    { id: 'settings', label: 'Settings', Icon: SettingsIcon },
];

export function App() {
    const [auth, setAuth] = useState<AuthState>({ kind: 'checking' });
    const [tab, setTab] = useState<Tab>('home');
    const [devVoice, setDevVoice] = useState(false);
    const themePreference = useThemePreference();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (import.meta.env.DEV && params.has('dev')) {
            const faceGrid = params.get('faceGrid');
            if (faceGrid === 'cartoon' || faceGrid === 'oled') {
                setAuth({ kind: 'dev-face-grid', which: faceGrid });
                return;
            }
            if (params.has('voice')) {
                setDevVoice(true);
            }
            void loadPal();
            return;
        }
        let cancelled = false;

        // Check existing session.
        isSignedIn().then((ok) => {
            if (cancelled) return;
            if (ok) void loadPal();
            else setAuth({ kind: 'signed-out' });
        });

        // ── Deep-link auth (navivibe://auth/callback?token=...) ──────────────
        // When the magic-link email is opened in Chrome, the backend interstitial
        // page redirects to navivibe://. Android opens the Vibe Pal app and fires
        // appUrlOpen. We POST the token directly from the native WebView so the
        // session cookie lands in the right (Capacitor) cookie store.
        const API_BASE: string =
            (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://navi-vibe.com';

        async function consumeDeepLinkToken(url: string) {
            try {
                const u = new URL(url);
                if (u.protocol !== 'navivibe:') return;
                const token = u.searchParams.get('token');
                if (!token) return;
                const res = await fetch(
                    `${API_BASE}/api/auth/verify?token=${encodeURIComponent(token)}`,
                    { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'include' },
                );
                if (res.ok && !cancelled) void loadPal();
            } catch { /* ignore */ }
        }

        // Cold-start: app launched via the deep link (was not running).
        CapApp.getLaunchUrl()
            .then((result) => { if (result?.url) void consumeDeepLinkToken(result.url); })
            .catch(() => {});

        // Warm/hot: app already running, brought to foreground via the deep link.
        const listenerPromise = CapApp.addListener('appUrlOpen', (event) => {
            void consumeDeepLinkToken(event.url);
        });

        return () => {
            cancelled = true;
            listenerPromise.then((h) => h.remove()).catch(() => {});
        };
    }, []);

    async function loadPal() {
        setAuth({ kind: 'signed-in', pal: 'loading' });
        try {
            const pal = await api.palMe();
            setAuth({ kind: 'signed-in', pal: pal ?? 'needs-onboard' });
        } catch {
            // Treat fetch failure as signed-out so the user can re-auth.
            setAuth({ kind: 'signed-out' });
        }
    }

    function onPalUpdated(p: PalProfileResponse) {
        if (auth.kind === 'signed-in') setAuth({ kind: 'signed-in', pal: p });
    }
    function onPalReset() {
        setAuth({ kind: 'signed-in', pal: 'needs-onboard' });
        setTab('home');
    }
    function onSignOut() {
        setAuth({ kind: 'signed-out' });
    }

    if (import.meta.env.DEV && auth.kind === 'dev-face-grid') {
        const Grid = auth.which === 'cartoon' ? DevFaceGridCartoon : DevFaceGrid;
        return (
            <main className="min-h-screen bg-bg px-3 py-3 text-text">
                <Suspense fallback={<p className="muted">Loading face grid...</p>}>
                    {Grid ? <Grid /> : null}
                </Suspense>
            </main>
        );
    }

    return (
        <div className="min-h-screen flex flex-col"
             style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'var(--safe-bottom)' }}>
            <header className="px-5 py-4 flex items-center gap-3">
                <NaviPulseMark />
                <h1 className="text-xl font-semibold">Vibe Pal</h1>
                <span className="ml-auto muted text-sm">
                    {auth.kind === 'signed-in' && typeof auth.pal === 'object' ? auth.pal.pal_name : ''}
                </span>
            </header>

            <main className="flex-1 px-5 pb-24 max-w-md mx-auto w-full">
                {auth.kind === 'checking' && <p className="muted">Loading…</p>}
                {auth.kind === 'signed-out' && (
                    <MagicLinkForm onSent={() => { /* user follows email */ }} />
                )}
                {auth.kind === 'signed-in' && auth.pal === 'loading' && (
                    <p className="muted">Finding your Pal…</p>
                )}
                {auth.kind === 'signed-in' && auth.pal === 'needs-onboard' && (
                    <OnboardingWizard onComplete={(p) => setAuth({ kind: 'signed-in', pal: p })} />
                )}
                {auth.kind === 'signed-in' && typeof auth.pal === 'object' && (
                    <SignedInBody
                        pal={auth.pal}
                        tab={tab}
                        devVoice={devVoice}
                        onPalUpdated={onPalUpdated}
                        onPalReset={onPalReset}
                        onSignOut={onSignOut}
                        onNavigate={(target) => setTab(target)}
                        themePreference={themePreference}
                    />
                )}
            </main>

            {auth.kind === 'signed-in' && typeof auth.pal === 'object' && !devVoice && (
                <BottomNav tab={tab} setTab={setTab} />
            )}
        </div>
    );
}

function SignedInBody({ pal, tab, devVoice, onPalUpdated, onPalReset, onSignOut, onNavigate, themePreference }: {
    pal: PalProfileResponse;
    tab: Tab;
    devVoice: boolean;
    onPalUpdated: (p: PalProfileResponse) => void;
    onPalReset: () => void;
    onSignOut: () => void;
    onNavigate: (target: HomeNavigationTarget) => void;
    themePreference: UseThemePreferenceState;
}) {
    const askMoji = useAskMojiThread(pal.pal_id);

    if (import.meta.env.DEV && devVoice) {
        return (
            <Suspense fallback={<p className="muted">Loading voice harness...</p>}>
                {DevVoiceHarness ? <DevVoiceHarness pal={pal} /> : null}
            </Suspense>
        );
    }

    // DriveTab is always mounted (never swapped out) so its BLE connection
    // survives tab switches. The hidden attribute hides it without unmounting.
    return (
        <>
            <div hidden={tab !== 'drive'}>
                <DriveTab
                    pal={pal}
                    active={tab === 'drive'}
                    renderVoiceTurn={askMoji.renderVoice}
                    appendVoiceTurn={askMoji.appendTurn}
                />
            </div>
            {tab === 'home'     && (
                <VibeHome
                    pal={pal}
                    onPalUpdated={onPalUpdated}
                    askMoji={askMoji}
                    onNavigate={onNavigate}
                />
            )}
            {tab === 'quests'   && <QuestDashboard />}
            {tab === 'profile'  && <VibeProfileScreen pal={pal} onPalUpdated={onPalUpdated} />}
            {tab === 'settings' && (
                <SettingsScreen
                    onPalReset={onPalReset}
                    onSignOut={onSignOut}
                    themePreference={themePreference}
                />
            )}
        </>
    );
}

function BottomNav({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
    return (
        <nav
            className="fixed inset-x-0 bottom-0 border-t border-line bg-surface/95 backdrop-blur"
            style={{ paddingBottom: 'var(--safe-bottom)' }}
        >
            <div className="max-w-md mx-auto flex">
                {TABS.map((t) => {
                    const Icon = t.Icon;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={`flex-1 py-3 text-xs font-medium transition ${
                                tab === t.id ? 'text-brand' : 'text-muted'
                            }`}
                            aria-pressed={tab === t.id}
                        >
                            <Icon className="mx-auto mb-0.5 h-5 w-5" aria-hidden strokeWidth={2.2} />
                            {t.label}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
}
